// Microphone permission helpers.
//
// Browsers only show the native "Allow microphone?" prompt when getUserMedia is
// called, and only reliably when that call happens inside the user-gesture task
// that started it. Once a user has denied, the prompt never reappears, the only
// recovery is through browser/OS settings, and there is no web API to open those
// pages. So the platform-standard pattern (Meet, Zoom, ChatGPT voice) is:
//
//   1. request permission first, synchronously off the tap, before any
//      connection/setup work;
//   2. classify the failure precisely;
//   3. for a blocked mic, show a recovery sheet with the exact steps for the
//      user's browser/OS plus a retry button.
//
// This module owns steps 1 and 2 plus the instruction text for step 3.

export const MIC_PERMISSION_STATE = {
  GRANTED: 'granted',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  UNAVAILABLE: 'unavailable',
  INSECURE: 'insecure',
  IN_USE: 'in-use',
  ERROR: 'error',
};

// Constraints must match the ones used for the real capture stream, otherwise
// some browsers treat the second getUserMedia call as a new request.
export const MIC_CONSTRAINTS = {
  audio: {
    channelCount: 1,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: false,
  },
};

const hasMediaDevices = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === 'function';

// getUserMedia is only exposed on secure origins. localhost is treated as
// secure, but a LAN IP over plain http is not, the classic "works on my
// laptop, missing on my phone" failure.
export const isSecureForMedia = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.isSecureContext) {
    return true;
  }
  const { protocol, hostname } = window.location;
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
};

// Non-blocking read of the current grant, where supported. Firefox and older
// Safari do not implement permissions.query({name:'microphone'}), so a null
// result means "unknown", never "denied".
export const queryMicPermission = async () => {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return null;
  }
  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    return status?.state || null;
  } catch {
    return null;
  }
};

// ── Environment detection, used only to pick the right instruction list ──
const detectEnvironment = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as a Mac; the touch-point check separates them.
    (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome|CriOS/.test(ua) && !isEdge;
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isSafari = /Safari/.test(ua) && !isChrome && !isEdge && !isFirefox;

  return { isIOS, isAndroid, isChrome, isEdge, isFirefox, isSafari };
};

// Step-by-step recovery instructions for a blocked microphone. On iOS every
// browser runs on WebKit and permission lives in iOS Settings, so the iOS steps
// intentionally ignore which browser is in use.
export const buildMicRecoverySteps = () => {
  const { isIOS, isAndroid, isChrome, isEdge, isFirefox, isSafari } = detectEnvironment();

  if (isIOS) {
    return {
      title: 'Allow the microphone on iPhone or iPad',
      steps: [
        'Open the iPhone Settings app (grey gear icon).',
        'Scroll down and tap your browser, Safari or Chrome.',
        'Tap Microphone and choose Allow, or turn the Microphone switch on.',
        'Return here and tap Try again.',
      ],
      note: 'If you use Safari, you can also tap the “aA” icon in the address bar → Website Settings → Microphone → Allow.',
    };
  }

  if (isAndroid) {
    return {
      title: 'Allow the microphone on Android',
      steps: [
        'Tap the lock or sliders icon at the left of the address bar.',
        'Tap Permissions (or Site settings).',
        'Set Microphone to Allow, then reload the page.',
        'Come back here and tap Try again.',
      ],
      note: 'Still blocked? Open Android Settings → Apps → your browser → Permissions → Microphone → Allow.',
    };
  }

  if (isFirefox) {
    return {
      title: 'Allow the microphone in Firefox',
      steps: [
        'Click the microphone (or lock) icon in the address bar.',
        'Remove the “Blocked” setting next to Use the Microphone.',
        'Reload the page and tap Try again, Firefox will ask for permission.',
      ],
      note: null,
    };
  }

  if (isSafari) {
    return {
      title: 'Allow the microphone in Safari',
      steps: [
        'Open Safari → Settings → Websites → Microphone.',
        'Find this site in the list and set it to Allow.',
        'Reload the page and tap Try again.',
      ],
      note: 'Also check macOS System Settings → Privacy & Security → Microphone and make sure Safari is enabled.',
    };
  }

  // Chrome / Edge / other Chromium desktop.
  return {
    title: `Allow the microphone in ${isEdge ? 'Edge' : isChrome ? 'Chrome' : 'your browser'}`,
    steps: [
      'Click the lock or sliders icon at the left of the address bar.',
      'Set Microphone to Allow.',
      'Reload the page, then tap Try again.',
    ],
    note: 'On Windows, also check Settings → Privacy & security → Microphone and make sure desktop apps can use it.',
  };
};

// Requests microphone access and returns a classified outcome instead of
// throwing, so callers can render the right recovery UI.
//
// The returned stream (on success) is live and MUST be either used for capture
// or stopped by the caller, otherwise the recording indicator stays on.
export const requestMicrophoneAccess = async () => {
  if (!isSecureForMedia()) {
    return {
      state: MIC_PERMISSION_STATE.INSECURE,
      stream: null,
      message:
        'Voice needs a secure (https) connection. Open this site over https, browsers block microphone access on plain http.',
    };
  }

  if (!hasMediaDevices()) {
    return {
      state: MIC_PERMISSION_STATE.UNAVAILABLE,
      stream: null,
      message:
        'This browser does not support microphone capture. Try the latest Chrome, Safari, Edge, or Firefox.',
    };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    return { state: MIC_PERMISSION_STATE.GRANTED, stream, message: null };
  } catch (error) {
    const name = error?.name || '';

    // NotAllowedError covers both "user clicked Block" and "already blocked, so
    // the prompt never showed". SecurityError is the older Safari spelling.
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return {
        state: MIC_PERMISSION_STATE.DENIED,
        stream: null,
        message: 'Microphone access is blocked for this site.',
      };
    }

    // No microphone hardware attached / enabled.
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
      return {
        state: MIC_PERMISSION_STATE.UNAVAILABLE,
        stream: null,
        message:
          'No microphone was found. Connect or enable a microphone, then try again.',
      };
    }

    // Another app or tab holds the device (common on Windows).
    if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
      return {
        state: MIC_PERMISSION_STATE.IN_USE,
        stream: null,
        message:
          'Your microphone is being used by another app or browser tab. Close it and try again.',
      };
    }

    return {
      state: MIC_PERMISSION_STATE.ERROR,
      stream: null,
      message: error?.message || 'Could not access the microphone.',
    };
  }
};

// Convenience: release a stream obtained by requestMicrophoneAccess when it will
// not be used for capture.
export const stopStream = (stream) => {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
};
